import { BehaviorSubject, EMPTY, firstValueFrom, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  AppServiceEvent,
  ConfigService,
  PaymentStatusPreSubmit,
} from 'src/app/core';
import {
  Order,
  OrderDto,
  PaymentEntity,
  PaymentCreateDto,
  Province,
} from 'src/app/shared';
import { OrderService } from '../../services';
import { PaymentService } from './payment.service';

interface PaymentServiceEvent
  extends AppServiceEvent<
    'submit' | 'create' | 'update' | 'remove' | 'init',
    any
  > {}

@Injectable({ providedIn: 'root' })
export class ShoppingCartService {
  public status$ = new BehaviorSubject<PaymentServiceEvent>(
    this.generateEventObject('init')
  );

  private _paymentStatusSubject$!: BehaviorSubject<PaymentStatusPreSubmit>;
  private get _paymentStatusObject$(): Observable<PaymentStatusPreSubmit> {
    return new Observable((subscriber) => {
      const statusObject: PaymentStatusPreSubmit = {
        price: this.calculateSelectedPrice(),
        selectedCount: this._selectedOrderLists.length,
      };
      subscriber.next(statusObject);
    });
  }

  private _selectedOrderLists: Order[] = [];

  public selectedOrdersList$ = new BehaviorSubject(
    this._selectedOrderLists
  ).pipe(catchError(() => EMPTY));

  public get paymentStatus$(): Observable<PaymentStatusPreSubmit | undefined> {
    return this._paymentStatusSubject$.pipe(catchError(() => EMPTY));
  }

  /**
   * Download every province from api.
   */
  public async getProvinces(): Promise<Province[]> {
    const url = this.configService.getUrl('transformation.getProvinces');
    const provinces = await firstValueFrom(
      this.httpClient.get<Province[]>(url)
    );

    return provinces;
  }

  // Constructor ------------------------------>

  constructor(
    private configService: ConfigService,
    private orderService: OrderService,
    private httpClient: HttpClient,
    private paymentService: PaymentService
  ) {
    this._paymentStatusSubject$ = new BehaviorSubject(
      this.getInitializerPaymentStatusInstance()
    );
  }

  /**
   * Select order means: add order instance to service
   * internal Order list.
   */
  public selectOrder(order: Order) {
    try {
      const index = this.getOrderIndex(order);
      if (index === -1) this._selectedOrderLists.push(order);
      else return;
    } catch (error) {
      throw error;
    } finally {
      this.emitPaymentStatusChange();
    }
  }

  public get selectedOrdersId(): number[] {
    return this._selectedOrderLists.map((order) => {
      return order.id;
    });
  }

  /**
   * Dis select means: removing order from service
   * internal order list.
   */
  public disSelectOrder(order: Order) {
    try {
      const index = this.getOrderIndex(order);
      if (index < 0) return;

      this.removeOrderFormList(order);
    } catch (error) {
      throw error;
    } finally {
      this.emitPaymentStatusChange();
    }
  }

  public getUserShoppingCartOrders(): Promise<
    (Order | OrderDto)[] | undefined
  > {
    return new Promise((resolve, reject) => {
      this.orderService.load({ justShoppingCart: true }).then((data) => {
        resolve(data);
      }, error => reject(error));
    });
  }

  public createPayment(dto: PaymentCreateDto): Observable<PaymentEntity> {
    return this.paymentService.create(dto);
  }

  // Private methods ---------------------------------->

  private generateEventObject(
    name: PaymentServiceEvent['name'],
    payload?: PaymentServiceEvent['payload']
  ): PaymentServiceEvent {
    return { name, payload };
  }

  private getInitializerPaymentStatusInstance(): PaymentStatusPreSubmit {
    const that = this;
    return {
      price: that.calculateSelectedPrice(),
      selectedCount: that._selectedOrderLists.length,
    };
  }

  private emitPaymentStatusChange() {
    this._paymentStatusObject$.subscribe((paymentStatus) => {
      this._paymentStatusSubject$.next(paymentStatus);
    });
  }

  private calculateSelectedPrice(): number {
    let price = 0;
    for (let item of this._selectedOrderLists) {
      price = price + parseInt(item.price);
    }
    return price;
  }

  private removeOrderFormList(order: Order) {
    const index = this.getOrderIndex(order);
    if (index < 0) return;

    this._selectedOrderLists.splice(index, 1);
  }

  private getOrderIndex(order: Order): number {
    return this._selectedOrderLists.indexOf(order);
  }
}
